import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import LoadingScreen from './components/LoadingScreen';
import './index.css';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import TicketsPage from './pages/TicketsPage';
import NewTicketPage from './pages/NewTicketPage';
import SsoTicketPage from './pages/SsoTicketPage';
import TicketDetailPage from './pages/TicketDetailPage';
import DeletedTicketsPage from './pages/DeletedTicketsPage';
import UsersPage from './pages/UsersPage';
import ProjectsPage from './pages/ProjectsPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import ReportsPage from './pages/ReportsPage';
import NotificationsPage from './pages/NotificationsPage';
import AuditLogPage from './pages/AuditLogPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import CheckInOutPage from './pages/CheckInOutPage';
import LeavePage from './pages/LeavePage';
import SLADashboardPage from './pages/SLADashboardPage';
import HolidayCalendarPage from './pages/HolidayCalendarPage';
import EscalationRulesPage from './pages/EscalationRulesPage';
import ProductivityPage from './pages/ProductivityPage';
import SubProjectsPage from './pages/SubProjectsPage';

// Protected Route wrapper
const ProtectedRoute = ({ children, roles }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;

  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();

  // Branded splash while we verify the session on first load
  if (loading) return <LoadingScreen />;

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" replace /> : <LoginPage />} />
      {/* External entry: ERP "Raise ITSM Ticket" lands here, auto-logs-in,
          then forwards to /tickets/new with the error as the subject. */}
      <Route path="/sso-ticket" element={<SsoTicketPage />} />
      <Route path="/" element={<Navigate to={user ? "/dashboard" : "/login"} replace />} />

      <Route path="/dashboard" element={
        <ProtectedRoute><DashboardPage /></ProtectedRoute>
      } />

      <Route path="/tickets" element={
        <ProtectedRoute><TicketsPage /></ProtectedRoute>
      } />

      <Route path="/tickets/new" element={
        <ProtectedRoute><NewTicketPage /></ProtectedRoute>
      } />

      {/* Ticket History. Both are declared before '/tickets/:id' so the static
          segments aren't read as a ticket id. */}
      <Route path="/tickets/closed" element={
        <ProtectedRoute><TicketsPage lockedStatus="closed" /></ProtectedRoute>
      } />

      <Route path="/tickets/deleted" element={
        <ProtectedRoute roles={['admin']}><DeletedTicketsPage /></ProtectedRoute>
      } />

      <Route path="/tickets/:id" element={
        <ProtectedRoute><TicketDetailPage /></ProtectedRoute>
      } />

      <Route path="/projects" element={
        <ProtectedRoute><ProjectsPage /></ProtectedRoute>
      } />

      <Route path="/users" element={
        <ProtectedRoute roles={['admin']}><UsersPage /></ProtectedRoute>
      } />

      <Route path="/reports" element={
        <ProtectedRoute roles={['admin', 'consultant']}>
          <ReportsPage />
        </ProtectedRoute>
      } />

      <Route path="/knowledge-base" element={
        <ProtectedRoute><KnowledgeBasePage /></ProtectedRoute>
      } />

      <Route path="/notifications" element={
        <ProtectedRoute><NotificationsPage /></ProtectedRoute>
      } />

      <Route path="/audit" element={
        <ProtectedRoute roles={['admin']}><AuditLogPage /></ProtectedRoute>
      } />

      <Route path="/profile" element={
        <ProtectedRoute><ProfilePage /></ProtectedRoute>
      } />

      <Route path="/settings" element={
        <ProtectedRoute roles={['admin']}><SettingsPage /></ProtectedRoute>
      } />

      <Route path="/checkin" element={
        <ProtectedRoute roles={['consultant', 'admin']}><CheckInOutPage /></ProtectedRoute>
      } />

      <Route path="/leaves" element={
        <ProtectedRoute roles={['consultant', 'admin']}><LeavePage /></ProtectedRoute>
      } />

      <Route path="/sla" element={
        <ProtectedRoute roles={['admin', 'consultant']}><SLADashboardPage /></ProtectedRoute>
      } />

      <Route path="/sub-projects" element={
        <ProtectedRoute roles={['admin', 'consultant']}><SubProjectsPage /></ProtectedRoute>
      } />

      <Route path="/holidays" element={
        <ProtectedRoute roles={['admin']}><HolidayCalendarPage /></ProtectedRoute>
      } />

      <Route path="/escalation-rules" element={
        <ProtectedRoute roles={['admin']}><EscalationRulesPage /></ProtectedRoute>
      } />

      <Route path="/productivity" element={
        <ProtectedRoute roles={['admin']}><ProductivityPage /></ProtectedRoute>
      } />

      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
